import { oneLine } from 'common-tags';

import { Container, RichInline } from '@/atoms';
import { CardSlider, FeatureCard, Prose } from '@/molecules';

import {
  blockSpacing,
  eyebrowClass,
  sectionBgProps,
  textStyleClass,
} from '@/utils';
import { getRealUrl } from '@/utils/utils';

/** Header + grid 1/2/3 de `FeatureCard` (boxed, sin CTA). Cada tarjeta admite máscara `Forma`. */
export const ComponentRevisadasTarjetasDestacadas = ({
  Eyebrow,
  Titulo,
  Subtitulo,
  Descripcion,
  EyebrowEstilo,
  TituloEstilo,
  SubtituloEstilo,
  DescripcionEstilo,
  TarjetasTituloEstilo,
  TarjetasDescripcionEstilo,
  Tarjetas,
  Ancla,
  FondoSeccion,
  Espaciado,
  // Toggle Strapi opt-in: en móvil las tarjetas pasan de la columna apilada a
  // un SLIDER horizontal (scroll-snap), que se lee mejor que el 2-col
  // apretado. A partir de sm+ vuelve al grid 2/3 col. Default null/false →
  // tenants antiguos sin cambios (1 col apilada).
  MobileCompact,
}: any) => {
  if (!Tarjetas?.length) return null;
  const eyebrowCls = eyebrowClass(EyebrowEstilo);
  const tituloCls = textStyleClass(TituloEstilo);
  const subtituloCls = textStyleClass(SubtituloEstilo);
  const descripcionCls = textStyleClass(DescripcionEstilo);
  // Defaults a nivel de bloque — pick per-card gana.
  const tarjetasTituloCls = textStyleClass(TarjetasTituloEstilo);
  const tarjetasDescripcionCls = textStyleClass(TarjetasDescripcionEstilo);

  return (
    <Container
      wrapper
      classNameWrapper={blockSpacing(Espaciado)}
      id={Ancla}
      {...sectionBgProps(FondoSeccion)}
    >
      {(Eyebrow || Titulo || Subtitulo || Descripcion) && (
        <div className="mb-12 flex max-w-full flex-col gap-2 lg:mb-20">
          {(Eyebrow || Titulo) && (
            <div className="flex flex-col items-end self-start">
              {Eyebrow && (
                <RichInline
                  html={Eyebrow}
                  className={oneLine`font-accent text-primary-500 ${
                    eyebrowCls ?? 'text-3xl leading-10 font-normal'
                  }`}
                />
              )}
              {Titulo && (
                <RichInline
                  as="h2"
                  html={Titulo}
                  className={oneLine`text-headline text-text-primary font-primary ${
                    tituloCls ?? 'text-headline-h1 font-bold'
                  }`}
                />
              )}
            </div>
          )}
          {Subtitulo && (
            <RichInline
              as="p"
              html={Subtitulo}
              className={oneLine`text-body text-text-primary ${
                subtituloCls ?? 'text-body-l'
              }`}
            />
          )}
          {Descripcion && (
            <Prose
              className={oneLine`text-body text-text-primary ${
                descripcionCls ?? '[&_p]:text-body-l'
              }`}
              html={Descripcion}
            />
          )}
        </div>
      )}

      {(() => {
        const cards = Tarjetas.map((tarjeta: any, index: number) => {
          const formaSvgUrl = tarjeta.Forma?.Svg?.url
            ? getRealUrl(tarjeta.Forma.Svg.url)
            : undefined;
          const imagen = tarjeta.Imagen
            ? {
                src: getRealUrl(tarjeta.Imagen.url),
                alt: tarjeta.Imagen.alternativeText ?? tarjeta.Titulo,
              }
            : undefined;

          return (
            // En slider (mobile compacto) cada tarjeta ocupa 80% y hace
            // `snap-center` → queda centrada con la vecina asomando. A partir
            // de sm+ vuelve al grid: la 3a impar ocupa fila completa
            // (`col-span-2`), y en lg 3-col vuelve a columna normal.
            <li
              key={tarjeta.id ?? index}
              // Sin `h-full` en el slider mobile: una altura fija (100% de un
              // padre auto) desactivaría el `align-items: stretch` del flex y
              // las tarjetas saldrían de distinto alto. Sin altura, el stretch
              // las iguala. En grid (sm+) sí se usa `h-full`.
              className={oneLine`${
                MobileCompact
                  ? 'w-[80vw] shrink-0 snap-center sm:h-full sm:w-auto'
                  : 'h-full'
              } sm:last:[&:nth-child(odd)]:col-span-2 lg:last:[&:nth-child(odd)]:col-span-1`}
            >
              <FeatureCard
                image={imagen}
                imageShapeUrl={formaSvgUrl}
                title={tarjeta.Titulo}
                description={tarjeta.Descripcion}
                titleStyleClass={
                  textStyleClass(tarjeta.TituloEstilo) ?? tarjetasTituloCls
                }
                descriptionStyleClass={
                  textStyleClass(tarjeta.DescripcionEstilo) ??
                  tarjetasDescripcionCls
                }
              />
            </li>
          );
        });

        // Mobile: slider con scroll-snap centrado + bullets (CardSlider). Se
        // sale del padding del Container (`-mx-4`) para full-bleed y añade
        // `px-[10%]` para que la primera/última tarjeta puedan centrarse.
        // sm+ recupera el grid. `items-stretch` iguala el alto de todas.
        return MobileCompact ? (
          <CardSlider
            className={oneLine`scrollbar-hide -mx-4 flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto px-[10vw] pb-1 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3 lg:gap-5`}
          >
            {cards}
          </CardSlider>
        ) : (
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-5">
            {cards}
          </ul>
        );
      })()}
    </Container>
  );
};

ComponentRevisadasTarjetasDestacadas.displayName =
  'ComponentRevisadasTarjetasDestacadas';
